import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { APP_LOCALE } from "../../config/locale";
import {
  getWorkById,
  getWorkOrders,
  getWorkSubcontractors,
  type Work,
  type WorkOrderItem,
  type WorkSubcontractorItem,
} from "../../obras/obrasApi";
import {
  getScheduleTasks,
  type ScheduleTask,
} from "../../cronograma/cronogramaApi";
import {
  getWorkDiaryEntries,
  type WorkDiaryEntryDto,
} from "../../workDiary/workDiaryApi";
import { WorkSubNav } from "../Obras/components/WorkSubNav";

// ---------------------------------------------------------------------------
// Helpers (read-only presentation)
// ---------------------------------------------------------------------------

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const clampPercent = (value?: number | null): number =>
  Math.max(0, Math.min(100, Math.round(value ?? 0)));

// Cores do estado da tarefa do cronograma (por_iniciar / em_curso / concluida).
const scheduleBadgeClass = (statusCode: string): string => {
  if (statusCode === "concluida") return "bg-green-500/10 text-green-700";
  if (statusCode === "em_curso") return "bg-blue-500/10 text-blue-700";
  return "bg-muted text-secondary-text";
};

// Cores do estado de um artigo de obra (encomendas / subempreiteiros).
const itemBadgeClass = (statusCode: string): string => {
  if (statusCode === "faturado") return "bg-primary/10 text-primary";
  if (statusCode === "recebido" || statusCode === "instalado") {
    return "bg-green-500/10 text-green-700";
  }
  if (statusCode === "pedido" || statusCode === "confirmado") {
    return "bg-blue-500/10 text-blue-700";
  }
  return "bg-muted text-secondary-text";
};

// "Em curso" = encomenda que ainda não está concluída no terreno. Os estados
// terminais são instalado/faturado (WorkItemStatus). Os restantes
// (em_aberto/pedido/confirmado/recebido) continuam em curso.
const ORDER_TERMINAL_CODES = new Set(["instalado", "faturado"]);
const ORDER_RANK: Record<string, number> = {
  em_aberto: 1,
  pedido: 2,
  confirmado: 3,
  recebido: 4,
  instalado: 5,
  faturado: 6,
};

const Badge = ({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 text-xs flex-shrink-0 ${className}`}
    style={{ borderRadius: "var(--radius)" }}
  >
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// Independent per-section loader: each section loads on its own so a failure
// in one never breaks the others.
// ---------------------------------------------------------------------------

interface SectionState<T> {
  status: "loading" | "ready" | "error";
  data: T | null;
  error: string;
}

function useWorkSection<T>(
  workId: number,
  fetcher: (id: number) => Promise<T>,
  fallback: string,
): SectionState<T> {
  const [state, setState] = useState<SectionState<T>>({
    status: "loading",
    data: null,
    error: "",
  });

  useEffect(() => {
    if (!Number.isFinite(workId)) return;
    let cancelled = false;
    setState({ status: "loading", data: null, error: "" });
    fetcher(workId)
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data, error: "" });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          data: null,
          error:
            error instanceof Error && error.message ? error.message : fallback,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [workId, fetcher, fallback]);

  return state;
}

// Fetchers estáveis (referências de módulo) para não re-disparar os effects.
const fetchWork = (id: number): Promise<Work> => getWorkById(id);
const fetchTasks = (id: number): Promise<ScheduleTask[]> =>
  getScheduleTasks(id);
const fetchSubcontractors = (
  id: number,
): Promise<WorkSubcontractorItem[]> => getWorkSubcontractors(id);
const fetchOrders = (id: number): Promise<WorkOrderItem[]> =>
  getWorkOrders(id);
const fetchDiary = (id: number): Promise<WorkDiaryEntryDto[]> =>
  getWorkDiaryEntries(id).then((vm) => vm.entries ?? []);

// Estado partilhado de carregamento/erro/vazio de uma secção.
const sectionStatus = (
  status: "loading" | "ready" | "error",
  error: string,
  isEmpty: boolean,
  emptyText: string,
): ReactNode | null => {
  if (status === "loading") {
    return (
      <div className="text-secondary-text text-sm px-1 py-2">A carregar…</div>
    );
  }
  if (status === "error") {
    return (
      <div
        className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
        style={{ borderRadius: "var(--radius)" }}
      >
        {error}
      </div>
    );
  }
  if (isEmpty) {
    return (
      <div
        className="border border-border bg-card text-secondary-text text-sm px-4 py-6 text-center"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        {emptyText}
      </div>
    );
  }
  return null;
};

const CardRow = ({ children }: { children: ReactNode }) => (
  <div
    className="bg-card border border-border p-4"
    style={{ borderRadius: "var(--radius-card)" }}
  >
    {children}
  </div>
);

// ---------------------------------------------------------------------------

const SECTION_IDS = ["cronograma", "subempreiteiros", "encomendas", "notas"] as const;
type SectionId = (typeof SECTION_IDS)[number];

export function CampoObraView() {
  const navigate = useNavigate();
  const params = useParams();
  const workId = Number(params.workId);

  const [activeSection, setActiveSection] = useState<SectionId>("cronograma");

  const isValidWorkId = Number.isFinite(workId) && workId > 0;

  const work = useWorkSection(workId, fetchWork, "Não foi possível carregar a obra.");
  const tasks = useWorkSection(workId, fetchTasks, "Não foi possível carregar o cronograma.");
  const subs = useWorkSection(workId, fetchSubcontractors, "Não foi possível carregar os subempreiteiros.");
  const orders = useWorkSection(workId, fetchOrders, "Não foi possível carregar as encomendas.");
  const diary = useWorkSection(workId, fetchDiary, "Não foi possível carregar as notas de obra.");

  const taskList = tasks.data ?? [];
  const subList = subs.data ?? [];

  // Encomendas "em curso": remove os estados terminais e coloca as mais abertas
  // primeiro (por estado), depois pela data de necessidade.
  const openOrders = useMemo(() => {
    const list = (orders.data ?? []).filter(
      (order) => !ORDER_TERMINAL_CODES.has(order.statusCode),
    );
    return [...list].sort((a, b) => {
      const rankDiff =
        (ORDER_RANK[a.statusCode] ?? 99) - (ORDER_RANK[b.statusCode] ?? 99);
      if (rankDiff !== 0) return rankDiff;
      const aDate = a.needDate ?? "";
      const bDate = b.needDate ?? "";
      return aDate.localeCompare(bDate);
    });
  }, [orders.data]);

  // Notas de obra: mais recentes primeiro, limitadas às últimas 15.
  const recentNotes = useMemo(() => {
    const list = diary.data ?? [];
    return [...list]
      .sort((a, b) => {
        const byDate = (b.entryDate ?? "").localeCompare(a.entryDate ?? "");
        if (byDate !== 0) return byDate;
        return b.id - a.id;
      })
      .slice(0, 15);
  }, [diary.data]);

  const countLabel = (
    state: SectionState<unknown>,
    count: number,
  ): string => (state.status === "ready" ? ` (${count})` : "");

  const navItems = [
    { id: "cronograma", label: `Cronograma${countLabel(tasks, taskList.length)}` },
    { id: "subempreiteiros", label: `Subempreiteiros${countLabel(subs, subList.length)}` },
    { id: "encomendas", label: `Encomendas${countLabel(orders, openOrders.length)}` },
    { id: "notas", label: `Notas${countLabel(diary, recentNotes.length)}` },
  ];

  const workData = work.data;
  const progresso = clampPercent(workData?.progressPercent);

  if (!isValidWorkId) {
    return (
      <div className="p-4 md:p-6 space-y-4 md:space-y-6">
        <button
          type="button"
          onClick={() => navigate("/campo")}
          className="text-primary text-sm cursor-pointer"
        >
          ← Voltar às obras
        </button>
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          Obra inválida.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <button
        type="button"
        onClick={() => navigate("/campo")}
        className="text-primary text-sm cursor-pointer"
      >
        ← Voltar às obras
      </button>

      {/* Header da obra */}
      {work.status === "error" ? (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {work.error}
        </div>
      ) : (
        <div
          className="bg-card border border-border p-4 md:p-6"
          style={{
            borderRadius: "var(--radius-card)",
            boxShadow: "var(--elevation-sm)",
          }}
        >
          {work.status === "loading" ? (
            <div className="text-secondary-text text-sm">A carregar obra…</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <span
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    {workData?.code}
                  </span>
                  <h3 className="break-words">{workData?.name}</h3>
                  <p className="text-secondary-text mt-1 break-words">
                    {workData?.clientName}
                  </p>
                </div>
                <Badge className="bg-primary/10 text-primary">
                  {workData?.statusName?.trim() || "—"}
                </Badge>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Progresso
                  </span>
                  <span
                    style={{
                      fontWeight: "var(--font-weight-semi-bold)",
                      fontSize: "var(--text-caption)",
                    }}
                  >
                    {progresso}%
                  </span>
                </div>
                <div
                  className="w-full bg-muted rounded-full h-2"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{
                      width: `${progresso}%`,
                      borderRadius: "var(--radius)",
                    }}
                  ></div>
                </div>
              </div>

              <div className="mt-4 text-sm text-secondary-text">
                {formatDate(workData?.startDate)} — {formatDate(workData?.endDate)}
              </div>
            </>
          )}
        </div>
      )}

      {/* Comutador de secções */}
      <WorkSubNav
        items={navItems}
        activeId={activeSection}
        onChange={(id) => setActiveSection(id as SectionId)}
      />

      {/* Cronograma */}
      {activeSection === "cronograma" && (
        <div className="space-y-3">
          {sectionStatus(
            tasks.status,
            tasks.error,
            taskList.length === 0,
            "Sem tarefas no cronograma.",
          ) ??
            taskList.map((task) => (
              <CardRow key={task.id}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 break-words font-medium">
                    {task.name || "—"}
                  </div>
                  <Badge className={scheduleBadgeClass(task.statusCode)}>
                    {task.statusName || "—"}
                  </Badge>
                </div>
                <div className="mt-2 text-sm text-secondary-text">
                  {formatDate(task.plannedStartDate)} —{" "}
                  {formatDate(task.plannedEndDate)}
                </div>
                {task.responsible && (
                  <div className="mt-1 text-sm text-secondary-text break-words">
                    Responsável: {task.responsible}
                  </div>
                )}
              </CardRow>
            ))}
        </div>
      )}

      {/* Subempreiteiros */}
      {activeSection === "subempreiteiros" && (
        <div className="space-y-3">
          {sectionStatus(
            subs.status,
            subs.error,
            subList.length === 0,
            "Sem subempreiteiros nesta obra.",
          ) ??
            subList.map((item) => (
              <CardRow key={item.id}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 break-words font-medium">
                    {item.subcontractor || "—"}
                  </div>
                  <Badge className={itemBadgeClass(item.statusCode)}>
                    {item.statusName || "—"}
                  </Badge>
                </div>
                {item.description && (
                  <div className="mt-2 text-sm text-secondary-text break-words">
                    {item.description}
                  </div>
                )}
                {item.needDate && (
                  <div className="mt-1 text-sm text-secondary-text">
                    Necessidade: {formatDate(item.needDate)}
                  </div>
                )}
              </CardRow>
            ))}
        </div>
      )}

      {/* Encomendas em curso */}
      {activeSection === "encomendas" && (
        <div className="space-y-3">
          {sectionStatus(
            orders.status,
            orders.error,
            openOrders.length === 0,
            "Sem encomendas em curso.",
          ) ??
            openOrders.map((order) => (
              <CardRow key={order.id}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 break-words font-medium">
                    {order.description || "—"}
                  </div>
                  <Badge className={itemBadgeClass(order.statusCode)}>
                    {order.statusName || "—"}
                  </Badge>
                </div>
                <div className="mt-2 text-sm text-secondary-text">
                  {order.quantity} {order.unit}
                  {order.balance > 0 && (
                    <span> · falta {order.balance} {order.unit}</span>
                  )}
                </div>
                {order.supplier && (
                  <div className="mt-1 text-sm text-secondary-text break-words">
                    Fornecedor: {order.supplier}
                  </div>
                )}
                {(order.needDate || order.deliveryDate) && (
                  <div className="mt-1 text-sm text-secondary-text">
                    {order.needDate && <>Necessidade: {formatDate(order.needDate)}</>}
                    {order.needDate && order.deliveryDate && " · "}
                    {order.deliveryDate && <>Entrega: {formatDate(order.deliveryDate)}</>}
                  </div>
                )}
              </CardRow>
            ))}
        </div>
      )}

      {/* Notas de obra */}
      {activeSection === "notas" && (
        <div className="space-y-3">
          {sectionStatus(
            diary.status,
            diary.error,
            recentNotes.length === 0,
            "Sem notas de obra.",
          ) ??
            recentNotes.map((entry) => (
              <CardRow key={entry.id}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-foreground font-medium">
                    {formatDate(entry.entryDate)}
                  </div>
                  <div className="text-secondary-text text-sm break-words">
                    {entry.author?.name || "—"}
                  </div>
                </div>
                <p
                  className="text-foreground mt-2 break-words"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  {entry.content}
                </p>
                {entry.attachments && entry.attachments.length > 0 && (
                  <div className="mt-2 text-secondary-text text-xs">
                    {entry.attachments.length} anexo(s)
                  </div>
                )}
              </CardRow>
            ))}
        </div>
      )}
    </div>
  );
}
